import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, ProgressBar, Button, Tab, Tabs, Table, Alert, Spinner, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { db, auth } from '../firebase';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import LearnerNavbar from '../components/LearnerNavbar';
import DocumentStatusSummary from '../components/DocumentStatusSummary';
import LinkIdNumberModal from '../components/LinkIdNumberModal';
import AddressModal from '../components/AddressModal';

const LearnerProfile = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [userProgress, setUserProgress] = useState(null);
  const [modules, setModules] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [userAchievements, setUserAchievements] = useState([]);
  const [learningStats, setLearningStats] = useState({});
  const [showIdModal, setShowIdModal] = useState(false);
  const [idLinkedSuccess, setIdLinkedSuccess] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressUpdateSuccess, setAddressUpdateSuccess] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentUser) {
      loadProfileData();
    }
  }, [currentUser]);

  const loadProfileData = async () => {
    try {
      setLoading(true);

      // Fetch user data
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (!userDoc.exists()) {
        devError('User profile not found');
        return;
      }

      const userData = userDoc.data();
      const progress = {
        enrolledModules: userData.enrolledModules || [],
        completedModules: userData.completedModules || [],
        currentModule: userData.currentModule || null,
        moduleProgress: userData.contentProgress || {}
      };
      setUserProgress(progress);

      // Fetch modules
      const modulesSnapshot = await getDocs(collection(db, 'modules'));
      const modulesData = modulesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setModules(modulesData);

      // Fetch assignments
      if (progress.enrolledModules.length > 0) {
        const assignmentsSnapshot = await getDocs(collection(db, 'assignments'));
        const assignmentsData = assignmentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })).filter(assignment =>
          progress.enrolledModules.includes(assignment.moduleId)
        );
        setAssignments(assignmentsData);
      }

      // Calculate achievements (placeholder - real achievements may not exist yet)
      setUserAchievements([]);

      // Calculate learning stats
      const totalTimeSpent = Object.values(progress.moduleProgress).reduce((sum, moduleProgress) => {
        return sum + (moduleProgress.totalTimeSpent || 0);
      }, 0);

      const totalLessons = Object.values(progress.moduleProgress).reduce((sum, moduleProgress) => {
        return sum + (moduleProgress.completedLessons?.length || 0);
      }, 0);

      const averageProgress = progress.enrolledModules.length > 0 ?
        progress.enrolledModules.reduce((sum, moduleId) => {
          const moduleProgress = progress.moduleProgress[moduleId];
          return sum + (moduleProgress?.progress || 0);
        }, 0) / progress.enrolledModules.length : 0;

      setLearningStats({
        totalTimeSpent,
        totalLessons,
        averageProgress: Math.round(averageProgress),
        completedModules: progress.completedModules.length,
        enrolledModules: progress.enrolledModules.length
      });

    } catch (error) {
      devError('Error loading profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  // ID number is optional — modal only opens if learner explicitly clicks "Link ID number"

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      devError('Failed to log out', error);
    }
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getModuleById = (moduleId) => {
    return modules.find(m => m.id === moduleId);
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'success';
    if (progress >= 60) return 'info';
    if (progress >= 40) return 'warning';
    return 'danger';
  };

  if (loading || !userProgress) {
    return (
      <>
        <LearnerNavbar />
        <Container className="mt-5">
          <div className="text-center py-5">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Loading profile...</span>
            </Spinner>
            <p className="mt-3 text-muted">Loading your profile...</p>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <LearnerNavbar />

      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">My Profile</h1>
              <p className="mission-text mb-4">
                Manage your learning profile and track achievements
              </p>
              <p className="mb-0">
                View your personal information, learning statistics, and completed achievements.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <p className="text-muted">Track your learning journey and achievements</p>
              </div>
              <Button variant="outline-secondary" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
            </div>
          </Col>
        </Row>

        {/* Success Alert for ID Linking */}
        {idLinkedSuccess && (
          <Row className="mb-3">
            <Col>
              <Alert variant="success" onClose={() => setIdLinkedSuccess(false)} dismissible>
                <strong>Success!</strong> Your ID number has been linked successfully. Documents will now be saved with your ID number.
              </Alert>
            </Col>
          </Row>
        )}

        {/* Profile Overview */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <div className="mb-3">
                  <div 
                    className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mx-auto"
                    style={{ width: '80px', height: '80px', fontSize: '2rem' }}
                  >
                    {(currentUser?.displayName || currentUser?.email || '').charAt(0).toUpperCase()}
                  </div>
                </div>
                <h5>{currentUser?.displayName || 'Learner'}</h5>
                <p className="text-muted">{currentUser?.email}</p>
                <Badge bg="primary">Active Learner</Badge>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={9}>
            <Row>
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h3 className="text-success">{learningStats.completedModules}</h3>
                    <small className="text-muted">Completed Modules</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h3 className="text-primary">{formatTime(learningStats.totalTimeSpent)}</h3>
                    <small className="text-muted">Study Time</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h3 className="text-info">{learningStats.totalLessons}</h3>
                    <small className="text-muted">Lessons Completed</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h3 className="text-warning">{userAchievements.length}</h3>
                    <small className="text-muted">Achievements</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Tabbed Content */}
        <Tabs defaultActiveKey="progress" id="profile-tabs" className="mb-4">
          <Tab eventKey="progress" title="Learning Progress">
            <Row>
              <Col>
                <Card>
                  <Card.Header>
                    <h5>Module Progress</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Module</th>
                          <th>Progress</th>
                          <th>Status</th>
                          <th>Time Spent</th>
                          <th>Last Access</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userProgress.enrolledModules.map(moduleId => {
                          const module = getModuleById(moduleId);
                          const moduleProgress = userProgress.moduleProgress[moduleId];
                          const isCompleted = userProgress.completedModules.includes(moduleId);
                          
                          return (
                            <tr key={moduleId}>
                              <td>
                                <strong>{module?.moduleName}</strong>
                                <br />
                                <small className="text-muted">{module?.description}</small>
                              </td>
                              <td>
                                <ProgressBar 
                                  now={moduleProgress?.progress || 0} 
                                  label={`${moduleProgress?.progress || 0}%`}
                                  variant={getProgressColor(moduleProgress?.progress || 0)}
                                />
                              </td>
                              <td>
                                {isCompleted ? (
                                  <Badge bg="success">Completed</Badge>
                                ) : (
                                  <Badge bg="primary">In Progress</Badge>
                                )}
                              </td>
                              <td>{formatTime(moduleProgress?.totalTimeSpent || 0)}</td>
                              <td>
                                {moduleProgress?.lastAccessDate ? 
                                  new Date(moduleProgress.lastAccessDate).toLocaleDateString() : 
                                  'Never'
                                }
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>

          <Tab eventKey="assignments" title="Assignments">
            <Row>
              <Col>
                <Card>
                  <Card.Header>
                    <h5>Assignment Progress</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Assignment</th>
                          <th>Due Date</th>
                          <th>Status</th>
                          <th>Progress</th>
                          <th>Score</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignments.map(assignment => {
                          const userSubmission = assignment.submissions?.[currentUser?.uid];
                          const progress = {
                            status: userSubmission?.status || 'not_started',
                            progress: userSubmission?.progress || 0,
                            score: userSubmission?.score
                          };
                          const dueDate = assignment.dueDate?.toDate ? assignment.dueDate.toDate() : new Date(assignment.dueDate);
                          const isOverdue = dueDate < new Date() && progress.status !== 'submitted' && progress.status !== 'graded';
                          
                          return (
                            <tr key={assignment.id} className={isOverdue ? 'table-danger' : ''}>
                              <td>
                                <strong>{assignment.title}</strong>
                                <br />
                                <small className="text-muted">{assignment.description}</small>
                                {isOverdue && <Badge bg="danger" className="ms-2">Overdue</Badge>}
                              </td>
                              <td>
                                <small>
                                  {new Date(assignment.dueDate).toLocaleDateString()}
                                  <br />
                                  {new Date(assignment.dueDate).toLocaleTimeString()}
                                </small>
                              </td>
                              <td>
                                <Badge bg={
                                  progress.status === 'completed' ? 'success' :
                                  progress.status === 'in_progress' ? 'primary' :
                                  isOverdue ? 'danger' : 'secondary'
                                }>
                                  {progress.status.replace('_', ' ').toUpperCase()}
                                </Badge>
                              </td>
                              <td>
                                <ProgressBar 
                                  now={progress.progress} 
                                  label={`${progress.progress}%`}
                                  variant={progress.progress === 100 ? 'success' : progress.progress > 50 ? 'info' : 'warning'}
                                  size="sm"
                                />
                              </td>
                              <td>
                                {progress.score !== null ? `${progress.score}/${assignment.maxScore}` : 'N/A'}
                              </td>
                              <td>
                                <Button 
                                  variant={progress.status === 'not_started' ? 'primary' : 'outline-primary'} 
                                  size="sm"
                                >
                                  {progress.status === 'completed' ? 'View' : progress.status === 'in_progress' ? 'Continue' : 'Start'}
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>

          <Tab eventKey="documents" title="Document Status">
            <Row>
              <Col>
                <DocumentStatusSummary />
              </Col>
            </Row>
          </Tab>

          <Tab eventKey="achievements" title="Achievements">
            <Row>
              <Col>
                <Card>
                  <Card.Header>
                    <h5>Your Achievements</h5>
                  </Card.Header>
                  <Card.Body>
                    {userAchievements.length > 0 ? (
                      <Row>
                        {userAchievements.map(achievement => (
                          <Col md={6} lg={4} key={achievement.id} className="mb-3">
                            <Card className="border-warning">
                              <Card.Body className="text-center">
                                <div className="mb-2" style={{ fontSize: '3rem' }}>
                                  {achievement.icon}
                                </div>
                                <h6>{achievement.name}</h6>
                                <p className="text-muted small">{achievement.description}</p>
                                <Badge bg="warning" text="dark">Earned</Badge>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    ) : (
                      <div className="text-center text-muted">
                        <p>No achievements yet. Keep learning to unlock achievements!</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>

          <Tab eventKey="certificates" title="Certificates">
            <Row>
              <Col>
                <Card>
                  <Card.Header>
                    <h5>Certificates</h5>
                  </Card.Header>
                  <Card.Body>
                    {userProgress.completedModules.length > 0 ? (
                      <Row>
                        {userProgress.completedModules.map(moduleId => {
                          const module = getModuleById(moduleId);
                          const moduleProgress = userProgress.moduleProgress[moduleId];
                          
                          return (
                            <Col md={6} key={moduleId} className="mb-3">
                              <Card className="border-success">
                                <Card.Body>
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                      <h6>{module?.moduleName}</h6>
                                      <p className="text-muted small">
                                        Completed: {moduleProgress?.completedAt ? 
                                          new Date(moduleProgress.completedAt).toLocaleDateString() : 
                                          'Unknown'
                                        }
                                      </p>
                                    </div>
                                    <Button variant="outline-success" size="sm">
                                      Download Certificate
                                    </Button>
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                          );
                        })}
                      </Row>
                    ) : (
                      <div className="text-center text-muted">
                        <p>No certificates yet. Complete a module to earn your first certificate!</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>

          <Tab eventKey="address" title="Address Information">
            <Row>
              <Col>
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Address Details</h5>
                    <Button variant="outline-primary" size="sm" onClick={() => setShowAddressModal(true)}>
                      Edit Address
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    {addressUpdateSuccess && (
                      <Alert variant="success" dismissible onClose={() => setAddressUpdateSuccess(false)}>
                        Address updated successfully!
                      </Alert>
                    )}

                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label><strong>Street Address</strong></Form.Label>
                          <p className="form-control-plaintext">
                            {userProfile?.streetAddress || <span className="text-muted">Not provided</span>}
                          </p>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label><strong>City</strong></Form.Label>
                          <p className="form-control-plaintext">
                            {userProfile?.city || <span className="text-muted">Not provided</span>}
                          </p>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label><strong>Province</strong></Form.Label>
                          <p className="form-control-plaintext">
                            {userProfile?.province || <span className="text-muted">Not provided</span>}
                          </p>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label><strong>Postal Code</strong></Form.Label>
                          <p className="form-control-plaintext">
                            {userProfile?.postalCode || <span className="text-muted">Not provided</span>}
                          </p>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>
        </Tabs>
      </Container>

      {/* ID Number Linking Modal */}
      <LinkIdNumberModal
        show={showIdModal}
        onHide={() => setShowIdModal(false)}
        onSuccess={() => {
          setShowIdModal(false);
          setIdLinkedSuccess(true);
        }}
      />

      {/* Address Edit Modal */}
      <AddressModal
        show={showAddressModal}
        onHide={() => setShowAddressModal(false)}
        onSuccess={() => {
          setShowAddressModal(false);
          setAddressUpdateSuccess(true);
          loadProfileData(); // Refresh profile data
        }}
      />
    </>
  );
};

export default LearnerProfile;